import { useState, useEffect, useCallback, useMemo } from 'react';

export interface BoxTypeOption {
  name: string;
  displayName: string;
  description?: string;
}

// Default box types (fallback) - defined outside to prevent recreation
const defaultBoxTypes: BoxTypeOption[] = [
  { name: 'tuck-top-snap-lock', displayName: 'Tuck Top Snap Lock' },
  { name: 'autolock-bottom', displayName: 'Autolock Bottom' },
  { name: 'reverse-tuck', displayName: 'Reverse Tuck' },
  { name: 'mailer-box', displayName: 'Mailer Box' },
  { name: 'two-piece', displayName: 'Two-Piece Box' },
  { name: 'pillow-box', displayName: 'Pillow Box' },
  { name: 'tray-only', displayName: 'Tray Only' },
  { name: 'gable-box', displayName: 'Gable Box' },
  { name: 'cake-box', displayName: 'Cake Box' }
];

export const useBoxTypes = () => {
  const [boxTypes, setBoxTypes] = useState<BoxTypeOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Memoize loadBoxTypes function to prevent recreation
  const loadBoxTypes = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const API_BASE_URL = import.meta.env.VITE_API_URL || '';
      const url = API_BASE_URL ? `${API_BASE_URL}/api/box-types/active` : '/api/box-types/active';
      const response = await fetch(url);
      
      if (!response.ok) {
        throw new Error('Failed to load box types');
      }
      
      const data = await response.json();
      
      if (data.success && data.boxTypes) {
        setBoxTypes(data.boxTypes);
      } else {
        // Use default box types as fallback
        setBoxTypes(defaultBoxTypes);
      }
    } catch (err) {
      console.error('Error loading box types:', err);
      setError('Failed to load box types. Using defaults.');
      // Use default box types as fallback
      setBoxTypes(defaultBoxTypes);
    } finally {
      setLoading(false);
    }
  }, []); // Empty deps - stable function

  // Load box types on mount
  useEffect(() => {
    loadBoxTypes();
  }, [loadBoxTypes]); // Safe - loadBoxTypes is memoized

  // Memoize reload function
  const reload = useCallback(() => {
    loadBoxTypes();
  }, [loadBoxTypes]);

  // Memoize the return object to prevent infinite re-renders
  return useMemo(() => ({
    boxTypes,
    loading,
    error,
    reload
  }), [boxTypes, loading, error, reload]);
};